import { createFileRoute, notFound, redirect, useLoaderData } from "@tanstack/react-router";
import { ReportPage } from "@/components/site/ReportPage";
import { REPORTS, type Report } from "@/content/reports";
import type { ReportDetails } from "@/content/report-details";
import { reportSchemas } from "@/lib/seo/page-schema";
import { truncateAtWord } from "@/lib/seo/truncate";

const REMOVED_SLUGS = new Set([
  "work-from-home",
  "medical-laboratories-in-bulgaria",
  "customer-behavior-during-a-state-of-emergency",
  "what-happens-when-people-are-gone",
  "the-most-important-covid-19-related-information-for-see-region",
  "the-most-popular-baby-products-a-social-media-analysis",
  "bulgarian-telecom-providers-a-social-media-analysis",
]);

export const Route = createFileRoute("/bg/reports/$slug")({
  loader: async ({ params }) => {
    if (params.slug === "index") {
      throw redirect({ to: "/bg/reports", search: { year: undefined, topic: undefined, role: undefined }, statusCode: 301 });
    }
    if (params.slug === "media-intelligence-banking-sector-bulgaria") {
      throw redirect({ to: "/bg/reports/$slug", params: { slug: "bulgarian-banking-industry-report" }, statusCode: 301 });
    }
    if (REMOVED_SLUGS.has(params.slug)) {
      throw notFound();
    }
    const report = REPORTS.find((r) => r.slug === params.slug);
    if (!report) throw notFound();
    // Dynamic imports keep ~70 KiB of report-details modules out of the
    // main/critical bundle; they only load when a BG report page is visited.
    const [{ REPORT_DETAILS }, { REPORT_DETAILS_BG }] = await Promise.all([
      import("@/content/report-details"),
      import("@/content/report-details-bg"),
    ]);
    const details = REPORT_DETAILS[params.slug];
    const bg = REPORT_DETAILS_BG[params.slug];
    if (!details) throw notFound();
    return { report, details, bg };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Доклад - Perceptica" }] };
    const { report, details, bg } = loaderData;
    const title = report.titleBg ?? report.title;
    const headline = bg?.headline ?? title;
    const desc = bg?.metaDescription ?? report.metaDescriptionBg ?? truncateAtWord(bg?.excerpt ?? bg?.intro?.[0] ?? details.intro[0] ?? "");
    const analystName = bg?.analystName ?? details.analystName;
    const image = bg?.heroImageSrc ?? details.heroImage.src;
    const ogImage = !image ? "" : image.startsWith("/") ? `https://perceptica.com${image}` : image;
    const canonical = `https://perceptica.com/bg/reports/${report.slug}`;
    return {
      meta: [
        { title: `${headline} | Доклад на Perceptica` },
        { name: "description", content: desc },
        { property: "og:title", content: headline },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        ...(ogImage ? [{ property: "og:image", content: ogImage }, { property: "og:image:alt", content: details.heroImage.alt }, { name: "twitter:image", content: ogImage }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: headline },
        { name: "twitter:description", content: desc },
      ],
      scripts: reportSchemas({
        locale: "bg",
        slug: report.slug,
        title: headline,
        description: desc,
        datePublished: bg?.publishedDate ?? details.publishedDate,
        analyst: analystName ? { name: analystName, role: bg?.analystRole } : undefined,
        image: ogImage || undefined,
        year: report.year,
        dateModified: report.updatedAtBg ?? report.updatedAt,
        topic: report.topic,
      }),
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-[760px] px-6 py-24 text-center">
      <h1 className="font-display text-3xl font-medium text-foreground">Докладът не е намерен</h1>
    </div>
  ),
  component: BgReportRoute,
});

function BgReportRoute() {
  const { report, details } = useLoaderData({ strict: false }) as {
    report: Report;
    details: ReportDetails;
  };
  return <ReportPage report={report} details={details} />;
}
